import React, { memo } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Chess.com-style Connection Signal Strength Bars.
 * 
 * - 4 green bars: Excellent (< 150ms)
 * - 3 green/lime bars: Good (150 - 300ms)
 * - 2 amber bars: Fair (300 - 600ms)
 * - 1 red bar: Poor (> 600ms)
 * - 0 bars / pulsing: Reconnecting or Disconnected
 * - Countdown pill: When opponent/player is disconnected
 */
export const ConnectionSignal = memo(({
  ping = null,
  isOnline = true,
  status = 'connected', // 'connected' | 'reconnecting' | 'offline'
  isDisconnected = false,
  countdown = null,
  size = 'md', // 'sm' | 'md' | 'lg'
  showPingText = false,
  className = ''
}) => {
  // Determine connection quality level (0 to 4 bars)
  // Bar color matches the player name text color (gray-100) instead of green
  let barCount = 4;
  let barColor = 'bg-gray-100';
  let qualityText = 'Excellent';

  if (!isOnline || status === 'offline' || isDisconnected) {
    barCount = 0;
    qualityText = isDisconnected ? 'Disconnected' : 'Offline';
  } else if (status === 'reconnecting') {
    barCount = 0;
    qualityText = 'Reconnecting...';
  } else if (typeof ping === 'number' && ping > 0) {
    if (ping < 150) {
      barCount = 4;
      barColor = 'bg-gray-100';
      qualityText = `Excellent (${Math.round(ping)}ms)`;
    } else if (ping < 300) {
      barCount = 3;
      barColor = 'bg-gray-100';
      qualityText = `Good (${Math.round(ping)}ms)`;
    } else if (ping < 600) {
      barCount = 2;
      barColor = 'bg-amber-400';
      qualityText = `Fair (${Math.round(ping)}ms)`;
    } else {
      barCount = 1;
      barColor = 'bg-rose-500';
      qualityText = `Slow (${Math.round(ping)}ms)`;
    }
  } else {
    // Default online state
    barCount = 4;
    barColor = 'bg-gray-100';
    qualityText = 'Connected';
  }

  // Size specifications for vertical signal bars (bold with smooth pill radius)
  const sizeMap = {
    sm: {
      gap: 'gap-[2px]',
      w: 'w-[2.5px]',
      h: ['h-[4px]', 'h-[7px]', 'h-[10px]', 'h-[13px]'],
      boxH: 'h-[14px]',
      countdownText: 'text-[10px] px-1 py-0.5'
    },
    md: {
      gap: 'gap-[2.5px]',
      w: 'w-[3.5px]',
      h: ['h-[5px]', 'h-[8px]', 'h-[11.5px]', 'h-[15px]'],
      boxH: 'h-[16px]',
      countdownText: 'text-xs px-1.5 py-0.5'
    },
    lg: {
      gap: 'gap-[3px]',
      w: 'w-[4.5px]',
      h: ['h-[6px]', 'h-[10px]', 'h-[14px]', 'h-[18px]'],
      boxH: 'h-[19px]',
      countdownText: 'text-xs px-2 py-0.5 font-bold'
    }
  };

  const currentSize = sizeMap[size] || sizeMap.md;

  // Disconnected state with countdown
  if (isDisconnected || !isOnline || status === 'offline') {
    return (
      <div 
        className={`inline-flex items-center gap-1.5 select-none ${className}`}
        title={countdown !== null && countdown !== undefined ? `Player disconnected (${countdown}s to forfeit)` : qualityText}
      >
        {countdown !== null && countdown !== undefined ? (
          <span className={`inline-flex items-center gap-1 rounded bg-rose-950/80 border border-rose-500/80 text-rose-300 font-mono ${currentSize.countdownText} animate-pulse`}>
            <AlertTriangle className="w-3 h-3 text-rose-400 shrink-0" />
            <span>{countdown}s</span>
          </span>
        ) : (
          <div className={`flex items-end ${currentSize.gap} ${currentSize.boxH}`}>
            {[0, 1, 2, 3].map((index) => (
              <span 
                key={index}
                className={`${currentSize.w} ${currentSize.h[index]} rounded-full bg-rose-500/35`} 
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Reconnecting state
  if (status === 'reconnecting') {
    return (
      <div 
        className={`inline-flex items-center gap-1 select-none ${className}`}
        title="Reconnecting to server..."
      >
        <div className={`flex items-end ${currentSize.gap} ${currentSize.boxH}`}>
          <span className={`${currentSize.w} ${currentSize.h[0]} rounded-full bg-amber-400 animate-pulse`} />
          <span className={`${currentSize.w} ${currentSize.h[1]} rounded-full bg-amber-400/30`} />
          <span className={`${currentSize.w} ${currentSize.h[2]} rounded-full bg-amber-400/30`} />
          <span className={`${currentSize.w} ${currentSize.h[3]} rounded-full bg-amber-400/30`} />
        </div>
        {showPingText && <span className="text-[10px] text-amber-300 font-mono">Reconnecting</span>}
      </div>
    );
  }

  // Standard Signal Strength Bars
  return (
    <div 
      className={`inline-flex items-center gap-1.5 select-none ${className}`}
      title={`Connection: ${qualityText}`}
    >
      <div className={`flex items-end ${currentSize.gap} ${currentSize.boxH}`}>
        {[0, 1, 2, 3].map((index) => {
          const isActive = index < barCount;
          return (
            <span
              key={index}
              className={`rounded-full transition-colors duration-200 ${currentSize.w} ${currentSize.h[index]} ${
                isActive ? barColor : 'bg-stone-600/40'
              }`}
            />
          );
        })}
      </div>
      {showPingText && ping && (
        <span className="text-[11px] font-mono text-stone-400">
          {Math.round(ping)}ms
        </span>
      )}
    </div>
  );
});

export default ConnectionSignal;
